import { BAUTEIL_QUALITAET, IMMOBILIEN_TYPES, objektSectionSchema, } from './schemas/pipeline';
import { RENDITE_FELDER, klasseVon } from './bewertungsklasse';
import { GEBAEUDETYPEN, HAUSTYPEN, WOHNUNGSTYPEN, ZUSTAND_BUILD_CONDITION, ZUSTAND_STUFEN, leseFunnelAdresse, objektMerkmaleSchema, } from './schemas/valuation';
import { LUXUS_STUFEN, MINERGIE_STUFEN, RENOVATION_KEINE, VOLUMEN_NORMEN, ZAEHLWEISEN, } from './avm';
/** Checkliste `immobilienTyp` → Bewertung `objektTyp`; Gewerbe/Grundstück have no model. */
export const MANDAT_OBJEKTTYP = {
    efh: 'efh',
    wohnung: 'ewg',
    mfh: 'mwg',
    gewerbe: null,
    grundstueck: null,
};
/**
 * Order CRM 25-9 (owner: «een switcher van eigentum en rendite» in the
 * Verkaufsmandate list) — the class of each Checkliste `immobilienTyp`. The
 * three types with an IAZI model go through the SAME two tables as the
 * Bewertung (`MANDAT_OBJEKTTYP` → `klasseVon`), so a type the Bewertung moves
 * to the other class moves here too: EFH and Wohnung are Eigentum, MFH is
 * Rendite. Grundstück has no class and stands only under «Alle».
 *
 * Gewerbe is Rendite HERE (owner 25-9: «gewerbe hoort bij rendite»; master
 * 11:17), written as its own line — deliberately NOT via
 * `MANDAT_OBJEKTTYP.gewerbe = 'mwg'`: that bridge feeds the IAZI model, where
 * Gewerbe has no model and keeps no class (`bewertungsklasse` stays
 * untouched). A Gewerbe object must never be valued as a Mehrfamilienhaus; it
 * only sorts with them in the mandate list.
 */
export const MANDAT_KLASSE = {
    efh: klasseVon(MANDAT_OBJEKTTYP.efh),
    wohnung: klasseVon(MANDAT_OBJEKTTYP.wohnung),
    mfh: klasseVon(MANDAT_OBJEKTTYP.mfh),
    gewerbe: 'rendite',
    grundstueck: klasseVon(MANDAT_OBJEKTTYP.grundstueck),
};
/**
 * The class of a Verkaufsmandat, read from its Checkliste `objekt` section —
 * the ONE place that decides it (master 25-9 10:38): the backend computes it
 * per row and filters `?klasse=` with it, and the list, its switcher and its
 * counts only read the result.
 *
 * The source is `objekt.immobilienTyp`. Deliberately NOT:
 *  - the column `mandates.objektTyp`: empty on prod for every row (measured
 *    25-9; nothing writes it);
 *  - `objekt.objektkategorie` (selbstbewohnt | renditeobjekt, E19): the owner
 *    chose the object type (25-9; master 11:17). A rented EFH is Eigentum.
 *
 * A Grundstück, and anything that is not one of the five types (absent, a
 * legacy word, a non-object section) → null: the mandate stands only under
 * «Alle».
 */
export function mandatKlasse(objekt) {
    if (typeof objekt !== 'object' || objekt === null || Array.isArray(objekt))
        return null;
    const typ = objekt.immobilienTyp;
    return IMMOBILIEN_TYPES.includes(typ)
        ? MANDAT_KLASSE[typ]
        : null;
}
/**
 * Order IAZI 25-9 + RV615 n2 (master 12:08): the Grundstücksfläche a Bewertung
 * takes from the mandate — ONE predicate for the prefill and the Checkliste
 * hint, and the floor the Bewertungsformular clamps a typed ground to. Two
 * bounds: OUR schema (`objektMerkmaleSchema`, the gate the server applies to
 * `object.merkmale`: positive, at most 1'000'000 — the IAZI spec itself sets no
 * bound on `surfaceGround`) and the floor of 1 m², because a ground under 1 m²
 * is no ground. The Checkliste takes 0 and has no ceiling (`flaechenSectionSchema`).
 */
export const GRUNDSTUECK_MIN = 1;
export const grundstueckUebernehmbar = (m2) => m2 >= GRUNDSTUECK_MIN && objektMerkmaleSchema.shape.grundstueck.safeParse(m2).success;
/**
 * Checkliste `zustand` (a sale's condition words) → Bewertung Zustand (the IAZI
 * buildCondition scale, B3-2). The word is the COARSE reading; where the
 * Checkliste rates the five Bauteile (see {@link zustandAusBauteilen}) that
 * finer picture wins. Master 16-9 16:05 (Egelberg back-test): the condition
 * verdict is a 13–15 % lever, so «gepflegt» (well kept, nothing due) is
 * Gut–Mittel 2.5 — not Mittel 3.0; «renovationsbedürftig» lands on
 * Mittel–Schlecht 3.5 (the Web Tool run of 12:05), not on Schlecht 4.0, which
 * the Web Tool reserves for a poor building. Decision point for hermeting —
 * ONE decision: the Bauteil table below derives the shared words from this one
 * (#578 MINOR-3), so the Egelberg re-measurement settles both paths at once.
 */
export const MANDAT_ZUSTAND = {
    neuwertig: 'sehr_gut',
    renoviert: 'gut',
    gepflegt: 'gut_mittel',
    renovationsbeduerftig: 'mittel_schlecht',
};
/** German labels of the four sale words — ONE list for the Checkliste form and the prefill hints. */
export const MANDAT_ZUSTAND_LABEL = {
    neuwertig: 'Neuwertig',
    renoviert: 'Renoviert',
    gepflegt: 'Gepflegt',
    renovationsbeduerftig: 'Renovationsbedürftig',
};
/**
 * Bauteil-`zustand` (neuwertig · gut · mittel · renovationsbeduerftig) → the
 * SAME condition scale as the object word. #578 MINOR-3: the two tables once
 * disagreed on «renovationsbedürftig» (3.5 as object word, 4.0 as Bauteil
 * verdict — about 7 % at the measured lever, depending only on which
 * Checkliste field the advisor filled). One word, one number: the words the two
 * vocabularies share are DERIVED from {@link MANDAT_ZUSTAND}; «gut» and
 * «mittel» are the plain scale points.
 */
export const BAUTEIL_ZUSTAND_RATING = {
    neuwertig: ZUSTAND_BUILD_CONDITION[MANDAT_ZUSTAND.neuwertig],
    gut: 2,
    mittel: 3,
    renovationsbeduerftig: ZUSTAND_BUILD_CONDITION[MANDAT_ZUSTAND.renovationsbeduerftig],
};
/** How many of the five Bauteile must be rated before their mean outranks the single word. */
export const BAUTEILE_MIN_BEWERTET = 3;
/** The ratings of the Bauteile that carry a KNOWN `zustand` word (unknown words and unrated parts are skipped). */
const bauteilRatings = (bauteile) => {
    const ratings = [];
    for (const teil of Object.values(bauteile ?? {})) {
        const z = teil?.zustand;
        if (z && z in BAUTEIL_ZUSTAND_RATING) {
            ratings.push(BAUTEIL_ZUSTAND_RATING[z]);
        }
    }
    return ratings;
};
/** How many Bauteile carry a known `zustand` word — the count the divergence hint names (#578 MINOR-4). */
export function anzahlBewerteteBauteile(bauteile) {
    return bauteilRatings(bauteile).length;
}
/**
 * The building condition from the Bauteil ratings (Küche/Bad/Boden/Fenster/
 * Mauerwerk): the mean of the rated parts, rounded to IAZI's half steps, as a
 * Zustand word — or `undefined` when fewer than {@link BAUTEILE_MIN_BEWERTET}
 * parts carry a `zustand` (then the coarse word applies). Rounding goes to the
 * NEAREST half step; an exact quarter (2.25) is a tie between 2.0 and 2.5 and
 * rounds UP to 2.5 — the half-up rule on doubled values (2.25·2 = 4.5 → 5 →
 * 2.5) — i.e. ties round toward the WORSE condition, the conservative reading.
 */
export function zustandAusBauteilen(bauteile) {
    const ratings = bauteilRatings(bauteile);
    if (ratings.length < BAUTEILE_MIN_BEWERTET)
        return undefined;
    const mittel = ratings.reduce((a, b) => a + b, 0) / ratings.length;
    const halbe = Math.round(mittel * 2) / 2;
    const index = Math.round((halbe - 1) * 2); // 1 → 0, 1.5 → 1, … 4 → 6
    return ZUSTAND_STUFEN[Math.min(Math.max(index, 0), ZUSTAND_STUFEN.length - 1)];
}
/** Checkliste `niedrigenergie` → Bewertung `minergie`; «andere» is not a Minergie code. */
export const MANDAT_MINERGIE = {
    nein: 'keine',
    minergie: 'minergie',
    minergie_p: 'minergie_p',
    minergie_a: 'minergie_a',
    andere: null,
};
/** Checkliste Bauteil-`qualitaet` → Bewertung Ausbaustandard (same four levels, two spellings). */
export const MANDAT_AUSBAUSTANDARD = {
    einfach: 'einfach',
    mittel: 'normal',
    gehoben: 'gehoben',
    luxurioes: 'luxus',
};
/**
 * The most frequent Bauteil quality (Küche/Bad/Boden/Fenster/Mauerwerk) — the
 * mandate rates five parts, the Bewertung wants ONE Ausbaustandard (analyse
 * §7.3: «meest voorkomende»). A tie goes to the LOWER level (order of
 * `BAUTEIL_QUALITAET`): the conservative reading, never an upgrade by
 * coincidence. `undefined` when no part is rated.
 */
export function haeufigsteBauteilQualitaet(bauteile) {
    if (!bauteile)
        return undefined;
    const teller = new Map();
    for (const teil of Object.values(bauteile)) {
        const q = teil?.qualitaet;
        if (q && BAUTEIL_QUALITAET.includes(q)) {
            const k = q;
            teller.set(k, (teller.get(k) ?? 0) + 1);
        }
    }
    let beste;
    let hoogste = 0;
    for (const k of BAUTEIL_QUALITAET) {
        const n = teller.get(k) ?? 0;
        if (n > hoogste) {
            hoogste = n;
            beste = k;
        }
    }
    return beste;
}
// ── B3-6: the other direction — a Bewertung seeds a fresh Verkaufsmandat ─────
//
// Order B3-6 (16-9): ONE object identity. Before a mandate exists the
// Bewertung is the object's record; the moment «Verkaufsmandat anlegen» runs
// from a Kontakt with a Bewertung, its object data is copied ONCE into the
// Checkliste and the Checkliste is the sole source from then on (B3-3 reads it
// back for the next valuation). The copy is a SEED: every value lands only in
// an empty Checkliste field, and the advisor reviews the wizard anyway.
/** Bewertung → Checkliste-Immobilientyp: the inverse of `MANDAT_OBJEKTTYP` on
 *  its three valuable rows (gewerbe/grundstueck have no Bewertung). */
export const OBJEKTTYP_NAAR_MANDAT = {
    efh: 'efh',
    ewg: 'wohnung',
    mwg: 'mfh',
};
/**
 * IAZI-Zustand (seven half-steps, B3-2) → the ONE Checkliste word (four).
 * Lossy on purpose — the Checkliste word is what the advisor confirms in the
 * wizard, the half-steps live in the Bewertung — but never by more than half a
 * step on the way back through `MANDAT_ZUSTAND` (pinned).
 */
export const ZUSTAND_NAAR_MANDAT = {
    sehr_gut: 'neuwertig',
    sehr_gut_gut: 'neuwertig',
    gut: 'renoviert',
    gut_mittel: 'gepflegt',
    mittel: 'gepflegt',
    mittel_schlecht: 'renovationsbeduerftig',
    schlecht: 'renovationsbeduerftig',
};
/** Minergie step → Checkliste-Niedrigenergie. The Eco variants collapse onto
 *  their base step (the Checkliste knows no Eco); the mapper says so in a hint. */
export const MINERGIE_NAAR_MANDAT = {
    keine: 'nein',
    minergie: 'minergie',
    minergie_p: 'minergie_p',
    minergie_eco: 'minergie',
    minergie_p_eco: 'minergie_p',
    minergie_a: 'minergie_a',
    minergie_a_eco: 'minergie_a',
};
const getal = (v) => typeof v === 'number' && Number.isFinite(v) ? v : undefined;
const tekst = (v) => typeof v === 'string' && v.trim() !== '' ? v.trim() : undefined;
const jaNein = (v) => v === true ? 'ja' : v === false ? 'nein' : undefined;
const uitLijst = (v, lijst) => typeof v === 'string' && lijst.includes(v) ? v : undefined;
const zet = (doel, veld, wert) => {
    // No '' guard here: every string arrives through `tekst`/`uitLijst`, which
    // already reject blanks — a guard nobody can reach is a guard nobody tests.
    if (wert !== undefined && wert !== null)
        doel[veld] = wert;
};
/**
 * The address `ort` is the BARE Gemeinde name (#585 MINOR-1): the Verlauf title,
 * the Maklervertrag merge and the Exposé read it. The public funnel writes its
 * label in a DISPLAY form «Boltigen (BE)» (immo-map `valuation.ts`), and a
 * dossier taken over from it copies that label — exactly ONE trailing «(KT)»
 * goes, so an official name like «Buchs (SG)» (funnel label «Buchs (SG) (SG)»)
 * keeps its own. An internal row carries the bare GWR name already (the CRM
 * sends `gemeindeName`), where a trailing «(SG)» IS the name — untouched.
 * ONLY the two origins known to write the display form strip (#585 hermeting
 * NIT-2): a missing or unknown origin fails to «do nothing», never to a
 * stripped official name. `objekt.gemeinde` (a display field) keeps the label.
 */
const KANTON_SUFFIX = /\s*\([A-Z]{2}\)$/;
const STRIPT_KANTON = new Set(['funnel', 'uebernommen']);
const ortUitLabel = (label, origin) => label !== undefined && typeof origin === 'string' && STRIPT_KANTON.has(origin)
    ? (tekst(label.replace(KANTON_SUFFIX, '')) ?? label)
    : label;
/**
 * Bewertung → Checkliste seeds (objekt · flaechen · energie · mandat.objektAdresse
 * + mandat.objektGwr.gemeindeBfs). Pure; the backend writes the seeds into
 * EMPTY fields only.
 */
export function bewertungNaarMandat(b) {
    const objekt = {};
    const flaechen = {};
    const energie = {};
    const mandat = {};
    const hinweise = [];
    const nichtUebernommen = [];
    const m = b.merkmale ?? {};
    const p = b.paidMerkmale ?? {};
    if (b.objektTyp in OBJEKTTYP_NAAR_MANDAT) {
        zet(objekt, 'immobilienTyp', OBJEKTTYP_NAAR_MANDAT[b.objektTyp]);
    }
    zet(objekt, 'titel', tekst(b.title));
    zet(objekt, 'gemeinde', tekst(b.label));
    zet(objekt, 'zimmer', getal(b.raeume));
    zet(objekt, 'baujahr', getal(b.baujahr));
    zet(flaechen, 'nettowohnflaeche', getal(b.wohnflaeche));
    const z = tekst(b.zustand);
    if (z) {
        if (ZUSTAND_STUFEN.includes(z)) {
            zet(objekt, 'zustand', ZUSTAND_NAAR_MANDAT[z]);
        }
        else {
            // The four legacy words (einfach/normal/gehoben/luxus, rows from before
            // B3-2) describe the Ausbaustandard, not the Zustand — no guess.
            hinweise.push(`Zustand «${z}» ist ein Ausbaustandard-Wort aus dem Altbestand — Zustand im Mandat bitte selbst setzen.`);
        }
    }
    // N3 (30-9): 0 = «Keine Renovation» is an answer, not a year — never a «0» in
    // the year field. Review 8-10 F5: the Checkliste has the answer now
    // («Sanierung» Nein/Ja): 0 → «nein», a year → «ja» + the year.
    const renovation = getal(p.renovationsjahr);
    if (renovation === RENOVATION_KEINE) {
        zet(objekt, 'sanierung', 'nein');
    }
    else if (renovation !== undefined) {
        zet(objekt, 'sanierung', 'ja');
        zet(objekt, 'letztesRenovationsjahr', renovation);
    }
    zet(objekt, 'haustyp', uitLijst(m.haustyp, HAUSTYPEN));
    zet(flaechen, 'grundstuecksflaeche', getal(m.grundstueck));
    zet(objekt, 'dachstockAusbaubar', jaNein(p.dachstockAusbaubar));
    zet(objekt, 'ferienobjekt', jaNein(p.ferienobjekt));
    zet(objekt, 'servitute', jaNein(p.servitute));
    zet(objekt, 'luxusobjekt', uitLijst(p.luxusobjekt, LUXUS_STUFEN));
    zet(objekt, 'zaehlweiseZimmer', uitLijst(p.zaehlweiseZimmer, ZAEHLWEISEN));
    zet(objekt, 'lageImOrt', getal(p.lageImOrt));
    zet(objekt, 'badezimmer', getal(p.badezimmer));
    // Micro-6 (18-9): the four EWG fields of the Web Tool — the Wohnung condition
    // through the same (lossy, pinned) word bridge as the building's Zustand.
    const wz = tekst(p.wohnungszustand);
    if (wz && ZUSTAND_STUFEN.includes(wz)) {
        zet(objekt, 'wohnungszustand', ZUSTAND_NAAR_MANDAT[wz]);
    }
    zet(objekt, 'lageImGebaeude', getal(p.lageImGebaeude));
    zet(objekt, 'wertquote', getal(p.wertquote));
    zet(objekt, 'gebaeudetyp', uitLijst(m.gebaeudetyp, GEBAEUDETYPEN));
    // IAZI-order 8-10 (B4): the HEDO fields the Checkliste has since 8.10. —
    // judged by the Checkliste's own field schema, so a seed always parses.
    zet(objekt, 'wohnungstyp', uitLijst(m.wohnungstyp, WOHNUNGSTYPEN));
    zet(objekt, 'maisonette', jaNein(m.maisonette));
    const bq = objektSectionSchema.shape.bauqualitaet.safeParse(p.bauqualitaet);
    if (bq.success)
        zet(objekt, 'bauqualitaet', bq.data);
    zet(objekt, 'separateWc', getal(p.separateWc));
    const etage = getal(p.etage);
    if (etage !== undefined)
        zet(objekt, 'etage', String(etage));
    zet(objekt, 'etagen', getal(p.geschosse));
    zet(objekt, 'wohneinheiten', getal(p.wohnungen));
    const verfall = tekst(p.baurechtVerfall);
    if (verfall) {
        // Baurecht in the Bewertung = the expiry date; in the Checkliste it is the
        // Eigentumsform plus that date (B3-4 reads it back the same way).
        zet(objekt, 'eigentumsform', 'baurecht');
        zet(objekt, 'baurechtVerfall', verfall);
    }
    // Order MFH-Rendite PR 2 (23-9): the Renditeliegenschaft's nine fields, 1:1
    // under the same names — and ONLY from an MFH row: the seeding is gated per
    // type exactly like the prefill, and the Checkliste shows them only at an
    // MFH (master 13:41). Each value is judged by the Checkliste's OWN field
    // schema, so a seed always parses as the section (the mix: the paid schema).
    if (b.objektTyp === 'mwg') {
        for (const feld of RENDITE_FELDER) {
            const r = objektSectionSchema.shape[feld].safeParse(p[feld]);
            if (r.success)
                zet(objekt, feld, r.data);
        }
    }
    zet(flaechen, 'balkonflaeche', getal(p.balkonflaeche));
    const volumen = getal(p.volumen);
    if (volumen !== undefined) {
        zet(flaechen, 'gebaeudevolumenGva', volumen);
        zet(flaechen, 'volumenNorm', uitLijst(p.volumenNorm, VOLUMEN_NORMEN));
    }
    zet(flaechen, 'garagenparkplaetze', getal(p.garagenparkplaetze));
    zet(flaechen, 'tiefgaragenplaetze', getal(p.tiefgaragenplaetze));
    zet(flaechen, 'aussenparkplaetze', getal(p.aussenparkplaetze));
    const minergie = uitLijst(p.minergie, MINERGIE_STUFEN);
    if (minergie) {
        zet(energie, 'niedrigenergie', MINERGIE_NAAR_MANDAT[minergie]);
        if (minergie.endsWith('_eco')) {
            hinweise.push('Minergie-Eco-Zusatz kennt die Checkliste nicht — als Basisstandard übernommen.');
        }
    }
    // The object address. A row that carries the address the visitor PICKED in
    // the funnel (owner 7-10: the funnel row and a dossier taken over from it)
    // gives street, PLZ and the POSTAL Ort from that STRUCTURE — never by
    // splitting the dossier's one display line «Strasse, PLZ Ort» in
    // `addressLine`: as a street that line made the Maklervertrag read the PLZ
    // and the Ort twice and opened the send gate on a line instead of a street
    // (RV661 MAJOR-1). The postal Ort may differ from the Gemeinde (3084 Wabern
    // in Köniz) — the contract wants the postal one.
    // Without it — an internal/paid row, a funnel row without a pick (manual
    // mode, before 7-10) or a dossier taken over before 7-10 — as before: the
    // street of an internal row (`addressLine`), PLZ and the BARE Gemeinde as Ort
    // (#584 MINOR-1, #585 MINOR-1 `ortUitLabel`), so the wizard's BFS gate sees
    // the Gemeinde boundary at the very first pick and the Verlauf title carries
    // its Ort. Such a mandate without a street stays stopped by the
    // Maklervertrag's send gate, which demands the street on this very field
    // (contract.service `send`, #585 MAJOR-1).
    const adresse = {};
    const gewaehlt = leseFunnelAdresse(b.funnelAdresse);
    if (gewaehlt) {
        zet(adresse, 'strasse', gewaehlt.strasse);
        zet(adresse, 'plz', gewaehlt.plz);
        zet(adresse, 'ort', gewaehlt.ort);
    }
    else {
        zet(adresse, 'strasse', tekst(b.addressLine));
        zet(adresse, 'plz', tekst(b.plz));
        zet(adresse, 'ort', ortUitLabel(tekst(b.label), b.origin));
    }
    if (Object.keys(adresse).length)
        mandat.objektAdresse = adresse;
    // #581 MINOR-3, decision (b): the Bewertung's BFS rides along as a register
    // fact (`mandat.objektGwr` is partial by design). The B3-3 prefill reads it
    // there (`m.gemeindeBfs ?? objektGwr.gemeindeBfs`) and would otherwise say
    // «BFS-Gemeindenummer fehlt» for a mandate born from a Bewertung WITH one.
    // The header column `mandates.gemeindeBfs` stays untouched (no writer here).
    const bfs = getal(b.gemeindeBfs);
    if (bfs !== undefined)
        mandat.objektGwr = { gemeindeBfs: bfs };
    // What the Checkliste has no field for — named, so nobody thinks it was copied.
    if (p.ausbaustandard !== undefined)
        nichtUebernommen.push('ausbaustandard');
    // (The Jahresmiete stood here until PR 2, Wohnungstyp and Maisonette until
    // 8-10 — they have their Checkliste fields now. The Lift stays: a yes/no is
    // not a number of Aufzüge.)
    if (m.lift !== undefined)
        nichtUebernommen.push('lift');
    const secties = {};
    if (Object.keys(objekt).length)
        secties.objekt = objekt;
    if (Object.keys(flaechen).length)
        secties.flaechen = flaechen;
    if (Object.keys(energie).length)
        secties.energie = energie;
    if (Object.keys(mandat).length)
        secties.mandat = mandat;
    return { secties, hinweise, nichtUebernommen };
}
/**
 * Every machine code `POST /mandates/aus-bewertung/:id` can answer with (B3-6;
 * #583 NIT-2). ONE source: the backend may only throw a code from this list
 * (typed), the CRM pins that every listed code has a German sentence — a code
 * thrown without being listed, or listed without a sentence, is caught on one
 * side or the other.
 */
export const MANDAT_AUS_BEWERTUNG_FEHLERCODES = [
    'bewertung_not_found',
    'bereits_verknuepft',
    'bereits_verknuepft_race',
    'kontakt_fehlt',
    'mandat_ohne_objektdaten',
    // Passed through from `MandateService.create` (the lead vanished between the
    // read and the create) — not this service's own refusal, but an answer the
    // endpoint can give (#584 NIT-1).
    'lead_not_found',
];
